import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { HexColorPicker } from 'react-colorful';

const GroupSettings = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#3B82F6');
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  useEffect(() => {
    fetchGroups();
  }, []);
  
  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/groups');
      
      if (response.data && response.data.groups) {
        setGroups(response.data.groups);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load groups. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddGroup = async (e) => {
    e.preventDefault();
    
    if (!newGroupName.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }
    
    try {
      const response = await api.post('/groups', {
        name: newGroupName,
        color: newGroupColor
      });
      
      if (response.data && response.data.group) {
        setGroups([...groups, response.data.group]);
        
        // Reset form
        setNewGroupName('');
        setNewGroupColor('#3B82F6');
        setShowAddForm(false);
        
        toast.success('Group added successfully');
      }
    } catch (error) {
      console.error('Error adding group:', error);
      toast.error(error.response?.data?.error || 'Failed to add group');
    }
  };
  
  const handleEditGroup = async (e) => {
    e.preventDefault();
    
    if (!newGroupName.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }
    
    try {
      const response = await api.put(`/groups/${editingGroup.id}`, {
        name: newGroupName,
        color: newGroupColor
      });
      
      if (response.data && response.data.group) {
        // Update groups list
        setGroups(
          groups.map(group => 
            group.id === editingGroup.id ? response.data.group : group
          )
        );
        
        // Reset form
        setEditingGroup(null);
        setNewGroupName('');
        setNewGroupColor('#3B82F6');
        setShowEditForm(false);
        
        toast.success('Group updated successfully');
      }
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error(error.response?.data?.error || 'Failed to update group');
    }
  };
  
  const handleDeleteGroup = async (groupId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this group? Projects in this group will be moved to the Default group.');
    
    if (!confirmDelete) return;
    
    try {
      await api.delete(`/groups/${groupId}`);
      
      // Remove from groups list
      setGroups(groups.filter(group => group.id !== groupId));
      
      toast.success('Group deleted successfully');
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(error.response?.data?.error || 'Failed to delete group');
    }
  };
  
  const handleEditClick = (group) => {
    setEditingGroup(group);
    setNewGroupName(group.name);
    setNewGroupColor(group.color);
    setShowEditForm(true);
    setShowAddForm(false);
  };
  
  const handleAddClick = () => {
    setShowAddForm(true);
    setShowEditForm(false);
    setNewGroupName('');
    setNewGroupColor('#3B82F6');
  };
  
  if (loading && groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48">
        <div className="w-10 h-10 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading groups...</p>
      </div>
    );
  }
  
  if (error && groups.length === 0) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={fetchGroups}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Groups</h2>
        <button
          type="button"
          onClick={handleAddClick}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Group
        </button>
      </div>
      
      {/* Add/Edit Form */}
      {(showAddForm || showEditForm) && (
        <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
          <h3 className="text-base font-medium text-gray-900 mb-3">
            {showAddForm ? 'Add New Group' : 'Edit Group'}
          </h3>
          <form onSubmit={showAddForm ? handleAddGroup : handleEditGroup}>
            <div className="flex space-x-4">
              <div className="flex-grow">
                <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  id="groupName"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                  placeholder="Enter group name"
                  disabled={editingGroup?.name === 'Default'}
                />
                {editingGroup?.name === 'Default' && (
                  <p className="text-xs text-orange-600 mt-1">
                    The Default group name cannot be changed.
                  </p>
                )}
              </div>
              <div className="w-48">
                <label htmlFor="groupColor" className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="relative">
                  <div 
                    className="w-full h-10 rounded-md border border-gray-300 cursor-pointer"
                    style={{ backgroundColor: newGroupColor }}
                    onClick={() => setShowColorPicker(!showColorPicker)}
                  ></div>
                  
                  {showColorPicker && (
                    <div className="absolute z-10 mt-1">
                      <HexColorPicker 
                        color={newGroupColor} 
                        onChange={setNewGroupColor}
                      />
                      <div className="flex mt-2 justify-end">
                        <button
                          type="button"
                          onClick={() => setShowColorPicker(false)}
                          className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-4 space-x-3">
              <button
                type="button"
                onClick={() => showAddForm ? setShowAddForm(false) : setShowEditForm(false)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-2 bg-blue-600 rounded-md text-sm text-white hover:bg-blue-700"
                disabled={editingGroup?.name === 'Default' && newGroupName === 'Default'}
              >
                {showAddForm ? 'Add Group' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Groups List */}
      {groups.length > 0 ? (
        <div className="overflow-hidden bg-white shadow sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {groups.map((group) => (
              <li key={group.id}>
                <div className="flex items-center px-4 py-4 sm:px-6">
                  <div className="flex items-center flex-1 min-w-0">
                    <div 
                      className="flex-shrink-0 w-6 h-6 rounded"
                      style={{ backgroundColor: group.color }}
                    ></div>
                    <div className="px-4 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {group.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        Created: {new Date(group.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleEditClick(group)}
                      className="ml-2 inline-flex items-center p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {group.name !== 'Default' && (
                      <button
                        onClick={() => handleDeleteGroup(group.id)}
                        className="ml-2 inline-flex items-center p-1.5 text-gray-500 hover:text-red-600 rounded-full hover:bg-gray-100"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <p className="text-gray-500">No groups have been created yet.</p>
        </div>
      )}
    </div>
  );
};

export default GroupSettings; 